
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, EmptyState } from "@/components/features/states";
import { toast } from "sonner";
import {
  Store,
  Bike,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  ShieldCheck,
  Upload,
} from "lucide-react";
import type { RoleApplication } from "@/lib/types";

export function RoleApplicationView() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"provider" | "courier">("courier");

  const { data, isLoading } = useQuery({
    queryKey: ["role-applications"],
    queryFn: () => api.roleApplications.list(),
  });
  const applications = data?.data?.applications ?? [];

  const submitMutation = useMutation({
    mutationFn: (body: any) => api.roleApplications.submit(body),
    onSuccess: (res) => {
      toast.success(t("roleApplication.submitted"));
      qc.invalidateQueries({ queryKey: ["role-applications"] });
    },
    onError: (e: any) => toast.error(e?.message || t("roleApplication.submitFailed")),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={t("roleApplication.title")}
        subtitle={t("roleApplication.subtitle")}
      />

      {/* Verification status overview */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <RoleStatusChip
              icon={ShieldCheck}
              label={t("roleApplication.sender")}
              verified={user?.senderVerified ?? true}
            />
            <RoleStatusChip
              icon={Store}
              label={t("roleApplication.provider")}
              verified={user?.providerVerified ?? false}
            />
            <RoleStatusChip
              icon={Bike}
              label={t("roleApplication.courier")}
              verified={user?.courierVerified ?? false}
            />
          </div>
        </CardContent>
      </Card>

      {/* Existing applications */}
      {applications.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold">{t("roleApplication.yourApplications")}</p>
          {applications.map((app) => (
            <ApplicationCard key={app.id} app={app} />
          ))}
        </div>
      )}

      {/* Already verified for both → nothing to do */}
      {user?.providerVerified && user?.courierVerified ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <p className="font-semibold">{t("roleApplication.allVerified")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Role tabs */}
          <div className="flex items-center rounded-full bg-muted p-1">
            <button
              onClick={() => setTab("courier")}
              disabled={user?.courierVerified}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition disabled:opacity-40 ${
                tab === "courier" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"
              }`}
            >
              <Bike className="h-3.5 w-3.5" /> {t("roleApplication.courier")}
            </button>
            <button
              onClick={() => setTab("provider")}
              disabled={user?.providerVerified}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition disabled:opacity-40 ${
                tab === "provider" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"
              }`}
            >
              <Store className="h-3.5 w-3.5" /> {t("roleApplication.provider")}
            </button>
          </div>

          {tab === "courier" ? (
            <CourierForm
              onSubmit={(body) => submitMutation.mutate({ role: "courier", ...body })}
              loading={submitMutation.isPending}
            />
          ) : (
            <ProviderForm
              onSubmit={(body) => submitMutation.mutate({ role: "provider", ...body })}
              loading={submitMutation.isPending}
            />
          )}
        </>
      )}
    </div>
  );
}

function RoleStatusChip({
  icon: Icon,
  label,
  verified,
}: {
  icon: any;
  label: string;
  verified: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full ${
          verified ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <span className="text-xs font-semibold">{label}</span>
      {verified ? (
        <Badge className="bg-emerald-500 text-[10px] text-white hover:bg-emerald-500">
          <CheckCircle2 className="mr-0.5 h-2.5 w-2.5" /> {t("common.verified")}
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[10px]">
          {t("common.unverified")}
        </Badge>
      )}
    </div>
  );
}

function ApplicationCard({ app }: { app: RoleApplication }) {
  const { t } = useTranslation();
  const statusConfig = {
    pending: { icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", label: t("roleApplication.statusPending") },
    approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", label: t("roleApplication.statusApproved") },
    rejected: { icon: XCircle, color: "text-red-600", bg: "bg-red-500/10", label: t("roleApplication.statusRejected") },
  };
  const cfg = statusConfig[app.status as keyof typeof statusConfig] ?? statusConfig.pending;
  const Icon = cfg.icon;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${cfg.bg} ${cfg.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold">
                {app.role === "courier" ? t("roleApplication.courier") : t("roleApplication.provider")}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(app.submittedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Badge className={`${cfg.bg} ${cfg.color} hover:${cfg.bg}`}>{cfg.label}</Badge>
        </div>
        {app.rejectionReason && (
          <p className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300">
            {app.rejectionReason}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ProviderForm({
  onSubmit,
  loading,
}: {
  onSubmit: (body: any) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [businessName, setBusinessName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [storePhotoUrl, setStorePhotoUrl] = useState("");
  const [bankingDetails, setBankingDetails] = useState("");

  const canSubmit = businessName.trim() && taxId.trim() && storePhotoUrl.trim();

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <Store className="h-5 w-5 text-primary" />
          <p className="font-semibold">{t("roleApplication.providerTitle")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.businessName")} *</Label>
          <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Ahla Water" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.taxId")} *</Label>
          <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="e.g. DJ-2026-00123" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.storePhotoUrl")} *</Label>
          <Input value={storePhotoUrl} onChange={(e) => setStorePhotoUrl(e.target.value)} placeholder="https://..." />
          <p className="text-xs text-muted-foreground">{t("roleApplication.storePhotoHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.bankingDetails")}</Label>
          <Textarea
            value={bankingDetails}
            onChange={(e) => setBankingDetails(e.target.value)}
            placeholder={t("roleApplication.bankingDetailsPlaceholder")}
            rows={2}
          />
        </div>
        <Button
          className="w-full"
          disabled={!canSubmit || loading}
          onClick={() => onSubmit({ businessName, taxId, storePhotoUrl, bankingDetails })}
        >
          {loading ? t("common.submitting") : t("roleApplication.submit")}
        </Button>
      </CardContent>
    </Card>
  );
}

function CourierForm({
  onSubmit,
  loading,
}: {
  onSubmit: (body: any) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licensePhotoUrl, setLicensePhotoUrl] = useState("");
  const [vehicleInsuranceUrl, setVehicleInsuranceUrl] = useState("");
  const [vehiclePhotoUrl, setVehiclePhotoUrl] = useState("");
  const [vehicleType, setVehicleType] = useState("motorcycle");

  const canSubmit =
    licenseNumber.trim() && licensePhotoUrl.trim() && vehicleInsuranceUrl.trim() && vehiclePhotoUrl.trim();

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <Bike className="h-5 w-5 text-primary" />
          <p className="font-semibold">{t("roleApplication.courierTitle")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.licenseNumber")} *</Label>
          <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="e.g. DJ-DL-2026-45678" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.vehicleType")} *</Label>
          <select
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
          >
            <option value="motorcycle">{t("jobCreate.vehicleMoto")}</option>
            <option value="car">{t("jobCreate.vehicleCar")}</option>
            <option value="badgag">{t("jobCreate.vehicleBadgag")}</option>
            <option value="pickup">{t("jobCreate.vehiclePickup")}</option>
            <option value="truck">{t("jobCreate.vehicleTruck")}</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.licensePhotoUrl")} *</Label>
          <Input value={licensePhotoUrl} onChange={(e) => setLicensePhotoUrl(e.target.value)} placeholder="https://..." />
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.vehicleInsuranceUrl")} *</Label>
          <Input value={vehicleInsuranceUrl} onChange={(e) => setVehicleInsuranceUrl(e.target.value)} placeholder="https://..." />
        </div>
        <div className="space-y-1.5">
          <Label>{t("roleApplication.vehiclePhotoUrl")} *</Label>
          <Input value={vehiclePhotoUrl} onChange={(e) => setVehiclePhotoUrl(e.target.value)} placeholder="https://..." />
        </div>
        <Button
          className="w-full"
          disabled={!canSubmit || loading}
          onClick={() => onSubmit({ licenseNumber, vehicleType, licensePhotoUrl, vehicleInsuranceUrl, vehiclePhotoUrl })}
        >
          {loading ? t("common.submitting") : t("roleApplication.submit")}
        </Button>
      </CardContent>
    </Card>
  );
}
